'use client';

import { useState } from 'react';
import { defineMessages } from 'react-intl';
import SUPPORTED_LANGUAGES from '@alltrails/core/constants/supportedLanguages';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import Select from '@alltrails/denali/components/Select';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import useLanguageChangeHandler from '../../../hooks/useLanguageChangeHandler';

const messages = defineMessages({
  LABEL: { defaultMessage: 'Select a language' }
});

type LanguageSelectDropdownProps = {
  languageRegionCode: LanguageRegionCode;
  enabledLanguages?: Array<string>;
  pageReloadOnLanguageChange?: boolean;
};

const defaultEnabledLanguages = ['de-DE', 'en-US', 'es-ES', 'fr-FR'];

const LanguageSelectDropdown = ({
  languageRegionCode,
  enabledLanguages = defaultEnabledLanguages,
  pageReloadOnLanguageChange
}: LanguageSelectDropdownProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState(languageRegionCode);
  const onLanguageChange = useLanguageChangeHandler(pageReloadOnLanguageChange);

  const {
    formattedDefaultMessages: { LABEL }
  } = useFormatMessage(messages);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(l => enabledLanguages.includes(l.languageRegionCode));

  const options = availableLanguages.map(l => ({
    label: l.translatedLanguageName,
    value: l.languageRegionCode,
    dataTestId: l.translatedLanguageName
  }));

  const handleSelectChangeNew = (newLanguageRegionCode: string) => {
    if (selectedLanguage === newLanguageRegionCode) {
      return;
    }

    setSelectedLanguage(newLanguageRegionCode as LanguageRegionCode);
    onLanguageChange(newLanguageRegionCode as LanguageRegionCode);
  };

  return (
    <div id="language-button">
      <Select
        options={options}
        value={selectedLanguage}
        onChange={handleSelectChangeNew}
        labelText={LABEL}
        placeholder={LABEL}
        size="sm"
        hideLabel
      />
    </div>
  );
};

export default LanguageSelectDropdown;
